'use client';

import { memo, useMemo, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { isInApp } from '@/libs/comeAppSDK';
import { loginTypesAtom } from '@/features/auth/store/login-type';
import { useAtomValue, useSetAtom } from 'jotai';
import { ThirdPartyButtonGroup, ThirdPartyButtonConfig } from './third-party-button-group';
import Image from 'next/image';
import FacebookPng from '../../assets/facebook_gray_v1.png';
import GooglePng from '../../assets/google_gray_v1.png';
import RainbowkitPng from '../../assets/metamask_gray.png';
import TelegramPng from '../../assets/telegram_gray_v1.png';
import { LoginType, useGetLoginType } from '../../services/login';

export interface ThirdPartyAuthButtonsDisplayProps {
  /** 额外的CSS类名 */
  className?: string;
}

/**
 * 第三方登录按钮纯展示组件
 * 仅用于展示，不包含任何业务逻辑和 provider 依赖
 * 适用于首页等不需要实际登录功能的地方
 */
const ThirdPartyAuthButtonsDisplayComponent = ({
  className,
}: ThirdPartyAuthButtonsDisplayProps) => {
  // 所有 hooks 必须在条件返回之前调用
  const t = useTranslations('auth');
  const setLoginTypes = useSetAtom(loginTypesAtom);
  const loginTypes = useAtomValue(loginTypesAtom);

  // 获取登录类型数据
  const { data: loginTypeData, isLoading } = useGetLoginType.useQueryHook({});

  // 保存登录类型到全局状态
  useEffect(() => {
    if (loginTypeData?.items) {
      setLoginTypes(loginTypeData.items || []);
    }
  }, [loginTypeData, setLoginTypes]);

  // 使用请求返回的数据，如果没有则使用 atom 中的数据
  const currentLoginTypes = loginTypeData?.items || loginTypes;

  // 过滤出支持的第三方登录类型
  const supportedLoginTypes = useMemo(() => [
    LoginType.LOGIN_TYPE_GOOGLE,
    LoginType.LOGIN_TYPE_FACEBOOK,
    LoginType.LOGIN_TYPE_TELEGRAM,
    LoginType.LOGIN_TYPE_RAINBOWKIT
  ], []);

  const supportedProviders = useMemo(() => 
    (currentLoginTypes || []).filter(item =>
      supportedLoginTypes.includes(item.loginType)
    ),
    [currentLoginTypes, supportedLoginTypes]
  );

  // 将提供商数据转换为按钮配置（仅展示，onClick 为空）
  const buttonConfigs: ThirdPartyButtonConfig[] = useMemo(() => 
    supportedProviders.map(provider => ({
      id: getProviderId(provider.loginType),
      text: getProviderTitle(provider.loginType, t),
      icon: getProviderIcon(provider.loginType),
      onClick: undefined, // 纯展示，不处理点击
    })),
    [supportedProviders, t]
  );

  // 检查是否在App内（在 hooks 之后）
  const inApp = isInApp();

  // 所有条件判断和返回都在 hooks 之后
  // 如果是在App内，不显示第三方登录
  if (inApp) {
    return null;
  }

  // 如果正在加载，不显示
  if (isLoading) {
    return null;
  }

  // 如果没有登录类型数据，不显示
  if (!currentLoginTypes || currentLoginTypes.length === 0) {
    return null;
  }

  // 如果没有支持的提供商，不显示
  if (supportedProviders.length === 0) {
    return null;
  }

  return (
    <ThirdPartyButtonGroup
      buttons={buttonConfigs}
      className={className}
    />
  );
};

/**
 * 根据 loginType 获取对应的提供商 ID
 */
const getProviderId = (loginType: number): string => {
  switch (loginType) {
    case LoginType.LOGIN_TYPE_GOOGLE:
      return 'google';
    case LoginType.LOGIN_TYPE_FACEBOOK:
      return 'facebook';
    case LoginType.LOGIN_TYPE_TELEGRAM:
      return 'telegram';
    case LoginType.LOGIN_TYPE_RAINBOWKIT:
      return 'rainbowkit';
    default:
      return 'unknown';
  }
};

/**
 * 根据 loginType 获取对应的提供商标题
 */
const getProviderTitle = (loginType: number, t: ReturnType<typeof useTranslations<'auth'>>): string => {
  switch (loginType) {
    case LoginType.LOGIN_TYPE_GOOGLE:
      return 'Google';
    case LoginType.LOGIN_TYPE_FACEBOOK:
      return 'Facebook';
    case LoginType.LOGIN_TYPE_TELEGRAM:
      return 'Telegram';
    case LoginType.LOGIN_TYPE_RAINBOWKIT:
      return t('wallet');
    default:
      return 'Unknown';
  }
};

/**
 * 根据 loginType 获取对应的图标组件
 */
const getProviderIcon = (loginType: number) => {
  switch (loginType) {
    case LoginType.LOGIN_TYPE_FACEBOOK:
      return <Image src={FacebookPng} alt="Facebook" width={40} height={40} className="object-contain" />;
    case LoginType.LOGIN_TYPE_GOOGLE:
      return <Image src={GooglePng} alt="Google" width={40} height={40} className="object-contain" />;
    case LoginType.LOGIN_TYPE_TELEGRAM:
      return <Image src={TelegramPng} alt="Telegram" width={40} height={40} className="object-contain" />;
    case LoginType.LOGIN_TYPE_RAINBOWKIT:
      return <Image src={RainbowkitPng} alt="Rainbow Kit" width={40} height={40} className="object-contain" />;
    default:
      return null;
  }
};

export const ThirdPartyAuthButtonsDisplay = memo(ThirdPartyAuthButtonsDisplayComponent);
